/* Ecommerce product form: variant resolution, state-safe server refresh, quantity and sticky add. */
(function () {
  'use strict';
  if (customElements.get('bb-product-form')) return;

  const BB = window.BB;

  class BBProductForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); }
      catch (e) { this.variants = []; }

      this.form = this.querySelector('form[data-bb-product-form]');
      if (!this.form) return;

      this.idInput = this.form.querySelector('[data-variant-id]');
      this.addBtn = this.form.querySelector('[data-add-to-cart]');
      this.addLabel = this.form.querySelector('[data-add-label]');
      this.formError = this.form.querySelector('[data-form-error]');
      this.delivery = this.form.querySelector('[data-delivery]');
      this.deliveryText = this.form.querySelector('[data-delivery-text]');
      this.priceWrap = this.querySelector('[data-price]');
      this.stickyPrice = document.querySelector('[data-sticky-price]');
      this.stickyAdd = document.querySelector('[data-sticky-add]');
      this.sectionId = this.getAttribute('data-section');
      this.variantRequest = null;
      this.requestId = 0;

      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-radio], [data-option-select]')) this.onOptionChange();
        if (e.target.matches('[data-qty-input]')) this.normalizeQuantity();
      });

      this.form.addEventListener('click', (e) => {
        const qtyButton = e.target.closest('[data-qty]');
        if (!qtyButton) return;
        const input = this.form.querySelector('[data-qty-input]');
        const current = parseInt(input.value || '1', 10);
        const delta = parseInt(qtyButton.getAttribute('data-qty'), 10);
        input.value = Math.max(1, (Number.isFinite(current) ? current : 1) + (Number.isFinite(delta) ? delta : 0));
      });

      if (this.stickyAdd) this.stickyAdd.addEventListener('click', () => this.addBtn && this.addBtn.click());
      if (!BB.config.cartEnabled || BB.config.cartType !== 'drawer') this.form.removeAttribute('data-bb-product-form');

      this.markAvailability(this.selectedOptions());
    }

    disconnectedCallback() {
      if (this.variantRequest) this.variantRequest.abort();
    }

    selectedOptions() {
      return Array.from(this.form.querySelectorAll('[data-option-index]')).map((field) => {
        const radio = field.querySelector('[data-option-radio]:checked');
        if (radio) return radio.value;
        const select = field.querySelector('[data-option-select]');
        return select ? select.value : null;
      });
    }

    normalizeQuantity() {
      const input = this.form.querySelector('[data-qty-input]');
      if (!input) return;
      const value = parseInt(input.value || '1', 10);
      input.value = Math.max(1, Number.isFinite(value) ? value : 1);
    }

    setError(message) {
      if (!this.formError) return;
      this.formError.textContent = message || '';
      this.formError.hidden = !message;
    }

    setBusy(busy) {
      this.form.setAttribute('aria-busy', String(busy));
      if (this.priceWrap) this.priceWrap.setAttribute('aria-busy', String(busy));
      if (this.delivery) this.delivery.setAttribute('aria-busy', String(busy));
      if (this.addBtn) this.addBtn.disabled = busy;
      if (this.stickyAdd) this.stickyAdd.disabled = busy;
    }

    setPurchaseState(available) {
      if (this.addBtn) this.addBtn.disabled = !available;
      if (this.stickyAdd) this.stickyAdd.disabled = !available;
      if (this.addLabel) this.addLabel.textContent = available ? BB.t('addToCart') : BB.t('soldOut');
      if (this.stickyAdd) this.stickyAdd.textContent = available ? BB.t('addToCart') : BB.t('soldOut');
    }

    onOptionChange() {
      const chosen = this.selectedOptions();
      this.form.querySelectorAll('[data-option-index]').forEach((field, index) => {
        const value = field.querySelector('[data-option-value]');
        if (value) value.textContent = chosen[index] || '';
      });

      const variant = this.variants.find((item) => item.options.every((option, index) => option === chosen[index]));
      this.markAvailability(chosen);

      if (!variant) {
        if (this.variantRequest) this.variantRequest.abort();
        this.requestId += 1;
        this.setUnavailable();
        return;
      }

      this.idInput.value = variant.id;
      if (this.stickyPrice) this.stickyPrice.textContent = BB.formatMoney(variant.price);

      const url = new URL(this.getAttribute('data-url'), location.origin);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);

      this.refreshVariant(url, variant);
    }

    refreshVariant(url, variant) {
      if (this.variantRequest) this.variantRequest.abort();
      this.variantRequest = new AbortController();
      const requestId = ++this.requestId;
      this.setError('');
      this.setBusy(true);

      fetch(url.pathname + '?variant=' + variant.id + '&section_id=' + this.sectionId, {
        signal: this.variantRequest.signal,
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('Variant section request failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          if (requestId !== this.requestId) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const price = doc.querySelector('[data-price]');
          const delivery = doc.querySelector('[data-delivery]');

          if (!price || !delivery) throw new Error('Variant section markup incomplete');

          if (this.priceWrap) this.priceWrap.innerHTML = price.innerHTML;
          if (this.delivery) {
            this.delivery.className = delivery.className;
            Array.from(this.delivery.attributes).forEach((attribute) => {
              if (attribute.name.startsWith('data-')) this.delivery.removeAttribute(attribute.name);
            });
            Array.from(delivery.attributes).forEach((attribute) => {
              if (attribute.name.startsWith('data-')) this.delivery.setAttribute(attribute.name, attribute.value);
            });
            const nextText = delivery.querySelector('[data-delivery-text]');
            if (nextText && this.deliveryText) this.deliveryText.innerHTML = nextText.innerHTML;
            const nextPrimary = delivery.querySelector('[data-bb-delivery-primary]');
            const currentPrimary = this.delivery.querySelector('[data-bb-delivery-primary]');
            const nextSecondary = delivery.querySelector('[data-bb-delivery-secondary]');
            const currentSecondary = this.delivery.querySelector('[data-bb-delivery-secondary]');
            if (nextPrimary && currentPrimary) currentPrimary.innerHTML = nextPrimary.innerHTML;
            if (nextSecondary && currentSecondary) currentSecondary.innerHTML = nextSecondary.innerHTML;
          }

          this.setBusy(false);
          this.setPurchaseState(Boolean(variant.available));
          document.dispatchEvent(new CustomEvent('bb:variant-changed', { detail: { variant, sectionId: this.sectionId } }));
        })
        .catch((error) => {
          if (error.name === 'AbortError' || requestId !== this.requestId) return;
          this.setBusy(false);
          this.setPurchaseState(false);
          this.setError(BB.t('variantUpdateError'));
        });
    }

    markAvailability(chosen) {
      this.form.querySelectorAll('[data-option-index]').forEach((field, index) => {
        field.querySelectorAll('[data-option-radio]').forEach((radio) => {
          const exists = this.variants.some((variant) => {
            if (!variant.available || variant.options[index] !== radio.value) return false;
            return variant.options.every((option, optionIndex) => optionIndex === index || chosen[optionIndex] === null || option === chosen[optionIndex]);
          });
          radio.disabled = !exists;
          const chip = radio.closest('.bb-chip');
          if (chip) chip.classList.toggle('bb-chip--unavailable', !exists);
        });

        const select = field.querySelector('[data-option-select]');
        if (select) {
          Array.from(select.options).forEach((optionEl) => {
            const exists = this.variants.some((variant) => {
              if (!variant.available || variant.options[index] !== optionEl.value) return false;
              return variant.options.every((option, optionIndex) => optionIndex === index || chosen[optionIndex] === null || option === chosen[optionIndex]);
            });
            optionEl.disabled = !exists;
          });
        }
      });
    }

    setUnavailable() {
      this.form.setAttribute('aria-busy', 'false');
      if (this.priceWrap) this.priceWrap.setAttribute('aria-busy', 'false');
      if (this.delivery) this.delivery.setAttribute('aria-busy', 'false');
      if (this.addBtn) this.addBtn.disabled = true;
      if (this.stickyAdd) {
        this.stickyAdd.disabled = true;
        this.stickyAdd.textContent = BB.t('unavailable');
      }
      if (this.addLabel) this.addLabel.textContent = BB.t('unavailable');
      this.setError('');
    }
  }

  customElements.define('bb-product-form', BBProductForm);
})();
